import { useEffect, useRef, useState } from "react"
import { AudioRecorderService } from "@/services/audio-recorder"
import { RecordingCapture } from "@/services/recording-capture"
import { acquireRecordingLock } from "@/services/recording-lock"
import { RecordingResilientUploader, UploadProgress } from "@/services/recording-resilient-uploader"
import { useMeetingRecordingStore } from "@/stores/use-meeting-recording-store"
import * as Sentry from "@sentry/nextjs"
import { toast } from "sonner"

import { ApiContext } from "@/hooks/api/base"
import { usePartnerAccess } from "@/hooks/use-partner-access"

interface UseStartRecordingOptions {
  meetingId: string
  meetingTitle?: string | null
  getContext: () => Promise<ApiContext | null>
  onProgress?: (progress: UploadProgress) => void
  onStarted?: () => void
  onError?: (error: Error) => void
}

export function useStartRecording() {
  const { enabled: fullProductAccess } = usePartnerAccess("full_product_access")
  const [isStarting, setIsStarting] = useState(false)
  const isMounted = useRef(true)
  const pendingStart = useRef<AbortController | null>(null)

  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
      pendingStart.current?.abort()
    }
  }, [])

  const start = async (options: UseStartRecordingOptions) => {
    if (!fullProductAccess) return
    const { meetingId, meetingTitle, getContext, onProgress, onStarted, onError } = options

    if (!isMounted.current || pendingStart.current) return

    const { isRecording, processingMeetingId } = useMeetingRecordingStore.getState()
    if (isRecording || processingMeetingId) {
      const error = new Error("A recording is already in progress")
      toast.error(error.message)
      onError?.(error)
      return
    }

    const startup = new AbortController()
    pendingStart.current = startup
    setIsStarting(true)

    let releaseCaptureLock: (() => Promise<void>) | undefined
    let stream: MediaStream | null = null
    let activeUploader: RecordingResilientUploader | null = null
    let capture: RecordingCapture | null = null
    let captureStarted = false
    let receivedChunk = false
    let startupStage = "capture_lock"
    const cancelStartup = () => {
      stream?.getTracks().forEach((track) => track.stop())
      void releaseCaptureLock?.()
    }
    startup.signal.addEventListener("abort", cancelStartup)
    try {
      releaseCaptureLock = await acquireRecordingLock("capture")
      startup.signal.throwIfAborted()
      startupStage = "microphone_permission"
      stream = await AudioRecorderService.requestMicrophonePermission()
      startup.signal.throwIfAborted()
      const { startRecording, ingestRecordingChunk, setUploader, setChunkProgress, setCapture, setCaptureWarning } =
        useMeetingRecordingStore.getState()
      startupStage = "create_recorder"
      const mediaRecorder = AudioRecorderService.createMediaRecorder(stream)

      const uploader = new RecordingResilientUploader({
        meetingId,
        meetingTitle,
        getContext,
        contentType: mediaRecorder.mimeType || "audio/webm",
        onProgress: (progress) => {
          if (startup.signal.aborted) return
          setChunkProgress({
            totalChunks: progress.totalChunks,
            uploadedChunks: progress.uploadedChunks,
            pendingChunks: progress.pendingChunks,
            lastConfirmedSequence: progress.lastConfirmedSequence,
            full: progress.full,
          })
          onProgress?.(progress)
        },
      })

      activeUploader = uploader
      startupStage = "create_session"
      await uploader.start()
      startup.signal.throwIfAborted()

      startupStage = "start_capture"
      capture = new RecordingCapture(mediaRecorder, {
        onChunk: (chunk) => {
          receivedChunk = true
          void ingestRecordingChunk(chunk)
        },
        onInterrupted: () => {
          void useMeetingRecordingStore.getState().finishRecording()
        },
        onWarning: (warning, diagnostics) => {
          setCaptureWarning(warning)
          if (warning)
            Sentry.captureMessage("Web recording capture needs attention", {
              level: "warning",
              tags: { feature: "web_recording", failure: warning },
              extra: { sessionGuid: uploader.getSessionGuid(), ...diagnostics },
            })
        },
        releaseCaptureLock,
      })
      capture.start()
      captureStarted = true
      // Ownership transfers to the global store only after startup succeeds.
      startup.signal.removeEventListener("abort", cancelStartup)
      pendingStart.current = null
      setUploader(uploader)
      setCapture(capture)
      startRecording(meetingId, meetingTitle ?? null, mediaRecorder, stream)
      toast.success("Recording started")
    } catch (error) {
      if (!startup.signal.aborted) {
        Sentry.captureException(error, {
          tags: { feature: "web_recording", failure: "startup", stage: startupStage },
          extra: { meetingId, sessionGuid: activeUploader?.getSessionGuid(), captureStarted, receivedChunk },
        })
      }
      stream?.getTracks().forEach((track) => track.stop())
      await releaseCaptureLock?.()
      if (startup.signal.aborted) {
        try {
          await activeUploader?.cleanup()
        } catch (cleanupError) {
          Sentry.captureException(cleanupError, {
            tags: { feature: "web_recording", failure: "cancel_startup" },
          })
        }
        return
      }
      if (!captureStarted) {
        try {
          await activeUploader?.cleanup()
        } catch (cleanupError) {
          // Keep local recovery state if the server could not acknowledge abandonment.
          Sentry.captureException(cleanupError, {
            tags: { feature: "web_recording", failure: "startup_cleanup" },
            extra: { meetingId, sessionGuid: activeUploader?.getSessionGuid() },
          })
        }
      } else {
        await activeUploader?.release()
      }
      const state = useMeetingRecordingStore.getState()
      if (capture && state.capture === capture) await state.stopRecording()
      await state.restorePendingUpload()
      if (startup.signal.aborted) return
      const err = error instanceof Error ? error : new Error("Failed to start recording")
      toast.error(err.message)
      onError?.(err)
      return
    } finally {
      startup.signal.removeEventListener("abort", cancelStartup)
      if (pendingStart.current === startup) pendingStart.current = null
      if (!startup.signal.aborted) setIsStarting(false)
    }
    onStarted?.()
  }

  return { start, isStarting }
}
